import { Injectable, NgZone } from '@angular/core';
import { ZIndex } from '../constants';

export interface SortableItem {
  element: HTMLElement;
  index: number;
}

export interface SortableResult {
  fromIndex: number;
  toIndex: number;
  items: any[];
  // * multi-drag support
  selectedIndexes?: number[];
  isMultiDrag?: boolean;
}

@Injectable()
export class SortableEngineService {
  private items: SortableItem[] = [];

  private isDragging = false;
  private container: HTMLElement | null = null;
  private draggedItem: SortableItem | null = null;
  private helper: HTMLElement | null = null;
  private placeholder: HTMLElement | null = null;
  private startPointer = { x: 0, y: 0 };
  private offset = { x: 0, y: 0 };
  private originalData: any[] = [];
  private targetIndex: number = -1;

  // * multi-drag support
  private selectedItems: SortableItem[] = [];
  private selectedDOMElements: HTMLElement[] = [];
  private hiddenElements: HTMLElement[] = [];
  private isMultiDrag = false;

  constructor(private ngZone: NgZone) {}

  // * callback for when sorting is complete
  onSorted: ((result: SortableResult) => void) | null = null;

  start(
    event: PointerEvent,
    container: HTMLElement,
    items: any[],
    dragHandle?: string,
  ): boolean {
    if (this.isDragging) return false;

    this.cleanup();

    const target = event.target as HTMLElement;
    const itemElement = this.findItemElement(target, container, dragHandle);
    if (!itemElement) return false;

    const itemIndex = this.getItemIndex(itemElement, container);
    if (itemIndex === -1) return false;

    this.isDragging = true;
    this.container = container;
    this.originalData = [...items];
    this.startPointer = { x: event.clientX, y: event.clientY };

    this.cacheItemElements(container);

    this.draggedItem =
      this.items.find((item) => item.index === itemIndex) || null;

    if (!this.draggedItem) {
      this.isDragging = false;
      return false;
    }

    // * detect multi-drag scenario using DOM "selection" classes
    this.detectSelectedItemsFromDOM();

    this.targetIndex = itemIndex;

    this.ngZone.runOutsideAngular(() => {
      // * calculate offset of the pointer -> used to prevent "mouse down jumping"
      const itemRect = this.draggedItem!.element.getBoundingClientRect();
      this.offset = {
        x: event.clientX - itemRect.left,
        y: event.clientY - itemRect.top,
      };

      // * create the floating dragegd item clone
      this.createHelper();
      this.createPlaceholder();

      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.body.style.userSelect = 'none';
    });

    // console.log(
    //   `%c DRAG START: Item ${itemIndex}`,
    //   'color: #00ff00; font-weight: bold',
    // );

    return true;
  }

  private onPointerMove = (event: PointerEvent) => {
    if (
      !this.isDragging ||
      !this.helper ||
      !this.placeholder ||
      !this.draggedItem
    ) {
      return;
    }

    // * Move visual helper under the cursor
    this.positionHelper(event);

    // * Determine current item under pointer
    const intersectedItem = this.getIntersectedItem(event.clientY);

    if (this.isValidIntersection(intersectedItem)) {
      this.swapPlaceholder(intersectedItem);
    } else {
      this.handleFreeSpaceMove();
    }
  };

  private onPointerUp = (event: PointerEvent) => {
    if (!this.isDragging) return;

    this.ngZone.run(() => {
      const placeholderDOMPosition = this.getPlaceholderDOMPosition();
      const result = this.calculateFinalOrder(placeholderDOMPosition);

      this.cleanup();

      if (this.onSorted) {
        this.onSorted(result);
      }
    });
  };

  // * Detect selected items using DOM classes
  private detectSelectedItemsFromDOM(): void {
    this.selectedItems = [];
    this.isMultiDrag = false;

    if (!this.container) return;

    const selectedElements = this.container.querySelectorAll(
      '.selected, [data-selected="true"], .widget-selected',
    );

    this.selectedDOMElements = Array.from(selectedElements) as HTMLElement[];

    selectedElements.forEach((element) => {
      const sortableItem = this.items.find(
        (item) => item.element === element || item.element.contains(element),
      );
      if (sortableItem && !this.selectedItems.includes(sortableItem)) {
        this.selectedItems.push(sortableItem);
      }
    });

    const includesDraggedItem = this.selectedItems.some(
      (item) => item.index === this.draggedItem?.index,
    );

    this.isMultiDrag = this.selectedItems.length > 1 && includesDraggedItem;
  }

  // * Cache element-to-index mapping
  private cacheItemElements(container: HTMLElement): void {
    this.items = [];
    const children = Array.from(container.children) as HTMLElement[];

    children.forEach((child, index) => {
      this.items.push({
        element: child,
        index,
      });
    });
  }

  // * Create floating "helper" clone -> supports multi-drag stacking
  private createHelper(): void {
    if (!this.draggedItem) return;

    if (this.isMultiDrag) {
      this.initMultiDragPreview();
    } else {
      this.initSingleDragPreview();
    }
  }

  private initSingleDragPreview(): void {
    if (!this.draggedItem) return;

    const itemRect = this.draggedItem.element.getBoundingClientRect();

    this.helper = this.draggedItem.element.cloneNode(true) as HTMLElement;
    this.helper.style.position = 'fixed';
    this.helper.style.zIndex = ZIndex.FULLSCREEN.toString();
    this.helper.style.pointerEvents = 'none';
    this.helper.style.width = `${itemRect.width}px`;
    this.helper.style.height = `${itemRect.height}px`;
    this.helper.style.left = `${this.startPointer.x - this.offset.x}px`;
    this.helper.style.top = `${this.startPointer.y - this.offset.y}px`;
    this.helper.style.opacity = '0.8';
    this.helper.classList.add('sortable-helper');

    // ! Copy computed styles to preserve layout when moved to body
    // ! This is needed when dragging plain divs (not self-contained Angular components)
    this.copyComputedStylesToHelper(this.helper, this.draggedItem.element);

    document.body.appendChild(this.helper);
  }

  // ! Copy critical computed styles from original to preview to preserve layout
  private copyComputedStylesToHelper(
    helper: HTMLElement,
    original: HTMLElement,
  ): void {
    // ! Copy styles for root element
    this.copyElementStyles(helper, original);

    // ! Recursively copy styles for direct children (prevents deep nesting issues)
    const helperChildren = Array.from(helper.children) as HTMLElement[];
    const originalChildren = Array.from(original.children) as HTMLElement[];

    helperChildren.forEach((helperChild, index) => {
      const originalChild = originalChildren[index];
      if (originalChild) {
        this.copyElementStyles(helperChild, originalChild as HTMLElement);
      }
    });
  }

  // ! Copy critical layout properties from one element to another
  private copyElementStyles(target: HTMLElement, source: HTMLElement): void {
    const computed = window.getComputedStyle(source);

    // ! Critical properties that often break when moved to <body>
    const criticalProps = [
      'display',
      'flex-direction',
      'flex-wrap',
      'align-items',
      'justify-content',
      'column-gap',
      'row-gap',
      'flex',
      'flex-grow',
      'flex-shrink',
      'flex-basis',
      'max-width',
      'min-width',
      'overflow',
      'white-space',
      'text-overflow',
    ];

    criticalProps.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value && value !== 'normal' && value !== 'none') {
        target.style.setProperty(prop, value);
      }
    });
  }

  private initMultiDragPreview(): void {
    if (!this.draggedItem || !this.selectedItems.length) return;

    const selection = [...this.selectedItems].sort((a, b) => a.index - b.index);
    const heights = selection.map(
      (item) => item.element.getBoundingClientRect().height,
    );
    const anchorIndex = selection.findIndex(
      (item) => item.index === this.draggedItem!.index,
    );

    this.hideSelectedSiblings();

    // ! Create helper container
    this.helper = document.createElement('div');
    Object.assign(this.helper.style, {
      position: 'fixed',
      zIndex: ZIndex.FULLSCREEN.toString(),
      pointerEvents: 'none',
      left: `${this.startPointer.x - this.offset.x}px`,
      top: `${this.startPointer.y - this.offset.y}px`,
    });
    this.helper.classList.add('sortable-helper', 'multi-drag-helper');

    const draggedRect = this.draggedItem.element.getBoundingClientRect();
    const gap = 6;

    // ! Create clones
    const clones = selection.map((item) => {
      const clone = item.element.cloneNode(true) as HTMLElement;
      Object.assign(clone.style, {
        position: 'absolute',
        width: `${draggedRect.width}px`,
        left: '0',
        display: '', // ! IMPORTANT: remove inherited display:none
        opacity: '0.8',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });

      clone.classList.add('multi-drag-clone');
      this.helper!.appendChild(clone);

      return clone;
    });

    let offset = 0;

    // ! position items ABOVE ANCHOR
    for (let i = anchorIndex - 1; i >= 0; i--) {
      offset -= heights[i] + gap;
      clones[i].style.top = `${offset}px`;
    }

    // ! position ANCHOR item
    clones[anchorIndex].style.top = '0px';

    // ! position items BELOW ANCHOR
    offset = heights[anchorIndex] + gap;
    for (let i = anchorIndex + 1; i < clones.length; i++) {
      clones[i].style.top = `${offset}px`;
      offset += heights[i] + gap;
    }

    document.body.appendChild(this.helper);
  }

  private hideSelectedSiblings(): void {
    this.hiddenElements = [];

    this.selectedItems.forEach((item) => {
      // ! don't hide the dragged item itself (it will be replaced by placeholder)
      if (item.index !== this.draggedItem?.index) {
        item.element.style.display = 'none';
        this.hiddenElements.push(item.element);
      }
    });
  }

  private createPlaceholder(): void {
    if (!this.draggedItem || !this.container) return;

    const itemRect = this.draggedItem.element.getBoundingClientRect();

    this.placeholder = document.createElement('div');
    this.placeholder.className = 'sortable-placeholder';
    this.placeholder.style.minHeight = `${itemRect.height}px`;
    this.placeholder.style.height = `${itemRect.height}px`;
    this.placeholder.style.margin = getComputedStyle(
      this.draggedItem.element,
    ).margin;
    this.placeholder.style.border = '2px dashed var(--color-border-selected)';
    this.placeholder.style.borderRadius = '4px';
    this.placeholder.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
    this.placeholder.style.opacity = '0.7';

    // * replace dragged item with placeholder
    this.draggedItem.element.parentElement?.replaceChild(
      this.placeholder,
      this.draggedItem.element,
    );
  }

  // * find which item the pointer intersects with
  // * uses live DOM queries to handle position changes after swapping
  private getIntersectedItem(pointerY: number): SortableItem | null {
    if (!this.container) return null;

    const children = Array.from(this.container.children) as HTMLElement[];

    for (const child of children) {
      // ! skip Placeholder and dragged Item
      if (child === this.placeholder || child === this.draggedItem?.element) {
        continue;
      }

      // * get live position data
      const rect = child.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      // ! 50% overlap required
      const tolerance = rect.height * 0.5;

      if (Math.abs(pointerY - midpoint) < tolerance) {
        // * find the original item data for this element
        const originalItem = this.items.find((item) => item.element === child);
        if (originalItem) {
          return originalItem;
        }
      }
    }

    return null;
  }

  private swapPlaceholder(intersectedItem: SortableItem): void {
    if (!this.placeholder || !this.draggedItem) return;

    const placeholder = this.placeholder;
    const target = intersectedItem.element;
    const container = this.container;

    // * update the target index
    this.targetIndex = intersectedItem.index;

    // * determine relative order -> top-to-bottom comparison
    const placeholderBeforeTarget =
      placeholder.compareDocumentPosition(target) &
      Node.DOCUMENT_POSITION_FOLLOWING;

    if (placeholderBeforeTarget) {
      container.insertBefore(placeholder, target.nextElementSibling);
    } else {
      container.insertBefore(placeholder, target);
    }

    // console.log(
    //   `%c Placeholder moved ${placeholderBeforeTarget ? 'down' : 'up'} near index ${this.targetIndex}`,
    //   'color:#ff9500;font-weight:bold;',
    // );
  }

  // * Handle case when cursor is in free space or over own placeholder
  private handleFreeSpaceMove(): void {
    if (!this.placeholder || !this.container) return;

    const children = Array.from(this.container.children);
    const placeholderIndex = children.indexOf(this.placeholder);

    // * when the cursor isn't over any item -> sync target index with the Placeholder
    if (placeholderIndex !== -1 && placeholderIndex !== this.targetIndex) {
      this.targetIndex = placeholderIndex;

      // console.log(
      //   `%c FREE-SPACE MOVE -> Target index = ${this.targetIndex}`,
      //   'color:#9c27b0; font-weight:bold',
      // );
    }
  }

  // * get Placeholder actual position -> counting only visible items (non-hidden)
  private getPlaceholderDOMPosition(): number {
    if (!this.placeholder || !this.container) return -1;

    const children = Array.from(this.container.children) as HTMLElement[];
    let visibleItemsBeforePlaceholder = 0;

    for (const child of children) {
      if (child === this.placeholder) break;

      if (child.style.display !== 'none') {
        visibleItemsBeforePlaceholder++;
      }
    }

    console.log(
      `%c Placeholder position: ${visibleItemsBeforePlaceholder} visible items before it`,
      'color: #9c27b0',
    );

    return visibleItemsBeforePlaceholder;
  }

  // * calculate final order based on tracked Target Index
  private calculateFinalOrder(placeholderDOMPosition?: number): SortableResult {
    if (!this.draggedItem) {
      return {
        fromIndex: 0,
        toIndex: 0,
        items: this.originalData,
      };
    }

    const fromIndex = this.draggedItem.index;
    const toIndex = this.targetIndex;

    if (this.isMultiDrag && this.selectedItems.length > 1) {
      return this.calculateMultiDragOrder(
        fromIndex,
        toIndex,
        placeholderDOMPosition,
      );
    }

    // * single Item drag
    const newItems = [...this.originalData];
    const [draggedData] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, draggedData);

    console.log(
      `%c Single Drag: ${fromIndex} -->> ${toIndex}`,
      'color: #2196F3; font-weight: bold',
    );

    return {
      fromIndex,
      toIndex,
      items: newItems,
      isMultiDrag: false,
    };
  }

  private calculateMultiDragOrder(
    fromIndex: number,
    toIndex: number,
    placeholderIndex?: number,
  ): SortableResult {
    const reorderedItems = [...this.originalData];

    // console.log('%c Drop target index:', 'color:#ff6b35', toIndex);

    // ! 1. Collect all selected items and their source indexes
    const selectedItems: any[] = [];
    const selectedIndexes: number[] = [];

    this.selectedDOMElements.forEach((el) => {
      const widgetId = this.getWidgetIdFromElement(el);
      if (!widgetId) return;

      const idx = reorderedItems.findIndex((item) => item.id === widgetId);
      if (idx !== -1) selectedIndexes.push(idx);
    });

    // ! 2. Extract them safely (descending order avoids index shift)
    const sortedIndexesDesc = [...selectedIndexes].sort((a, b) => b - a);
    for (const idx of sortedIndexesDesc) {
      const item = reorderedItems.splice(idx, 1)[0];
      selectedItems.unshift(item);
    }

    // console.log(
    //   '%c Extracted items:',
    //   'color:#35ff49',
    //   selectedItems.map((i) => i.title || i.id),
    // );

    // ! 3. Compute final insertion index
    const removedBeforeTarget = selectedIndexes.filter(
      (i) => i < toIndex,
    ).length;

    const finalIndex =
      placeholderIndex !== undefined && placeholderIndex >= 0
        ? placeholderIndex
        : toIndex - removedBeforeTarget;

    // console.log('%c Inserting block at index:', 'color:#ff6b35', finalIndex);

    // ! 4. Insert the selected block into its new position
    reorderedItems.splice(finalIndex, 0, ...selectedItems);

    // console.log(
    //   `%c Multi-drag complete -> ${finalIndex} (${selectedItems.length} items)`,
    //   'color:#ff6b35; font-weight:bold',
    // );

    return {
      fromIndex: fromIndex,
      toIndex: finalIndex,
      items: reorderedItems,
      selectedIndexes: selectedIndexes.sort((a, b) => a - b),
      isMultiDrag: true,
    };
  }

  private getWidgetIdFromElement(element: HTMLElement): string | null {
    if (element.dataset['widgetId']) {
      return element.dataset['widgetId'];
    }

    return null;
  }

  private cleanup(): void {
    if (this.helper) {
      this.helper.remove();
      this.helper = null;
    }

    // * restore original item at placeholder position
    if (this.placeholder && this.draggedItem) {
      this.placeholder.parentElement?.replaceChild(
        this.draggedItem.element,
        this.placeholder,
      );
      this.placeholder = null;
    }

    this.restoreHiddenElements();

    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.body.style.userSelect = '';

    // * reset state
    this.isDragging = false;
    this.draggedItem = null;
    this.container = null;
    this.targetIndex = -1;
    this.items = [];
    this.originalData = [];

    // * reset multi-drag state
    this.selectedItems = [];
    this.selectedDOMElements = [];
    this.hiddenElements = [];
    this.isMultiDrag = false;
  }

  private restoreHiddenElements(): void {
    this.hiddenElements.forEach((element) => {
      element.style.display = '';
    });
  }

  private findItemElement(
    target: HTMLElement,
    container: HTMLElement,
    dragHandle?: string,
  ): HTMLElement | null {
    let current = target;

    while (current && current !== container) {
      if (dragHandle && current.matches(dragHandle)) {
        while (current && current.parentElement !== container) {
          current = current.parentElement!;
        }

        return current;
      }

      if (!dragHandle && current.parentElement === container) {
        return current;
      }

      current = current.parentElement!;
    }

    return null;
  }

  private getItemIndex(
    itemElement: HTMLElement,
    container: HTMLElement,
  ): number {
    return Array.from(container.children).indexOf(itemElement);
  }

  private positionHelper(event: PointerEvent): void {
    this.helper!.style.left = `${event.clientX - this.offset.x}px`;
    this.helper!.style.top = `${event.clientY - this.offset.y}px`;
  }

  private isValidIntersection(item: SortableItem | null): boolean {
    return !!item && item.index !== this.draggedItem!.index;
  }

  get dragging(): boolean {
    return this.isDragging;
  }
}
